import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  Box,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Rating,
  Divider,
  Avatar,
  Button,
  CircularProgress,
} from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import BuildIcon from '@mui/icons-material/Build'
import CarRepairIcon from '@mui/icons-material/CarRepair'
import TimelineIcon from '@mui/icons-material/Timeline'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PhoneIcon from '@mui/icons-material/Phone'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import NotesIcon from '@mui/icons-material/Notes'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import dayjs from 'dayjs'
import { getDownloadURL, getStorage, ref } from '@firebase/storage'
import { OtwAgGridReact as AgGridReact } from '@/components/AgGrid/OtwAgGridReact'
import { ColDef } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import type { RepairShopLocation, RepairShopGrade, ShopType, Vehicle } from '@otw/models'
import { useLogistics, useRepairTrucksForShop } from '@/hooks/logistics/useLogistics'
import { getRepairShopExpenseTotal, useExpenses } from '@/hooks/useExpenses'
import { formatCurrency } from '@/utils/formatCurrency'
import { getTruckStatus } from '@otw/models/logistics'
import type { TransportTruckWithVehicle } from '@/hooks/logistics/useLogistics'
import TimelineDialog from '../../TransporterManagement/components/dialogs/TimelineDialog'

/** Format total minutes as timeInRepair string (e.g. "5d 2h"). */
function formatMinutesToTimeInRepair(totalMinutes: number): string {
  if (totalMinutes <= 0) return '—'
  const days = Math.floor(totalMinutes / (24 * 60))
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60)
  const minutes = Math.floor(totalMinutes % 60)
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

const getShopTypeLabel = (shopType: ShopType): string => {
  switch (shopType) {
    case 'mechanic_shop':
      return 'Mechanic'
    case 'body_shop':
      return 'Body Shop'
    case 'parts':
      return 'Parts'
    case 'mobile':
      return 'Mobile'
    case 'other':
      return 'Other'
    default:
      return 'Unknown'
  }
}

const getShopTypeIcon = (shopType: ShopType) => {
  switch (shopType) {
    case 'mechanic_shop':
      return <BuildIcon sx={{ fontSize: 14 }} />
    case 'body_shop':
      return <CarRepairIcon sx={{ fontSize: 14 }} />
    case 'mobile':
      return <DirectionsCarIcon sx={{ fontSize: 14 }} />
    default:
      return <LocationOnIcon sx={{ fontSize: 14 }} />
  }
}

/** Strong, readable colors used for shop-type chips on the gradient header. */
const getShopTypeColor = (shopType: ShopType, theme: Theme): string => {
  switch (shopType) {
    case 'mechanic_shop':
      return theme.palette.primary.main
    case 'body_shop':
      return theme.palette.secondary.main
    case 'parts':
      return theme.palette.info.main
    case 'mobile':
      return theme.palette.success.main
    default:
      return theme.palette.text.secondary
  }
}

const getGradeColor = (
  grade: RepairShopGrade,
): 'success' | 'warning' | 'error' | 'default' => {
  switch (grade) {
    case 'A':
      return 'success'
    case 'B':
      return 'warning'
    case 'C':
      return 'error'
    default:
      return 'default'
  }
}

const formatPhoneDigits = (raw?: string): string | null => {
  if (!raw) return null
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return raw
}

interface ShopMetricsDialogProps {
  open: boolean
  onClose: () => void
  location: RepairShopLocation | null
  /** When provided, shows an edit button that opens the shop edit form. */
  onEdit?: (location: RepairShopLocation) => void
}

interface TruckAtShopItem {
  vehicle: TransportTruckWithVehicle['vehicle']
  timeInRepair: string
  timeInRepairMinutes: number
}

interface ShopInvoiceItem {
  id: string
  storagePath: string
  uploadedAtMs: number
  uploadedAtLabel: string
  userName?: string
  note?: string
  vehicleLabel: string
  stockNumber?: string
  vin?: string
}

function ShopInvoicePdfLink({ storagePath }: { storagePath: string }) {
  const [url, setUrl] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')

  useEffect(() => {
    if (!storagePath) {
      setUrl(null)
      setStatus('idle')
      return
    }
    let cancelled = false
    setStatus('loading')
    getDownloadURL(ref(getStorage(), storagePath))
      .then((downloadUrl) => {
        if (cancelled) return
        setUrl(downloadUrl)
        setStatus('ready')
      })
      .catch(() => {
        if (cancelled) return
        setUrl(null)
        setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [storagePath])

  if (status === 'loading') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Loading invoice…
        </Typography>
      </Box>
    )
  }
  if (status === 'error' || !url) {
    return (
      <Typography variant="caption" color="error">
        Could not open invoice
      </Typography>
    )
  }

  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      size="small"
      variant="outlined"
      startIcon={<PictureAsPdfIcon fontSize="small" />}
      endIcon={<OpenInNewIcon fontSize="small" />}
      sx={{ textTransform: 'none' }}
    >
      Open invoice
    </Button>
  )
}

interface MetricCardProps {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
  color: string
}

const MetricCard: React.FC<MetricCardProps> = ({ icon, label, value, color }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      minWidth: { xs: '100%', sm: 150 },
      flex: 1,
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <Avatar
      variant="rounded"
      sx={{
        bgcolor: alpha(color, 0.12),
        color,
        width: 44,
        height: 44,
      }}
    >
      {icon}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
)

interface DetailItemProps {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
}

const DetailItem: React.FC<DetailItemProps> = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
    <Box sx={{ color: 'text.secondary', display: 'flex', mt: '2px' }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  </Box>
)

const ShopMetricsDialog: React.FC<ShopMetricsDialogProps> = ({
  open,
  onClose,
  location,
  onEdit,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [timelineVehicle, setTimelineVehicle] = useState<Vehicle | null>(null)
  const { transportTrucksWithVehicles } = useLogistics()
  const { expenses } = useExpenses({ options: { dontShowUpdates: true } })
  const { repairTrucks: shopRepairTrucks, isLoading: shopInvoicesLoading } = useRepairTrucksForShop(
    location?.id,
    { enabled: open },
  )

  const transportTruckVehicleById = useMemo(() => {
    const map = new Map<string, Vehicle>()
    transportTrucksWithVehicles.forEach((item) => {
      if (item.vehicle) map.set(item.truck.id, item.vehicle)
    })
    return map
  }, [transportTrucksWithVehicles])

  const shopInvoices = useMemo((): ShopInvoiceItem[] => {
    if (!location?.id) return []

    const items: ShopInvoiceItem[] = []
    shopRepairTrucks.forEach((repairTruck) => {
      const vehicle = transportTruckVehicleById.get(repairTruck.transport_truck_id)
      const vehicleLabel = vehicle
        ? `${vehicle.year} ${vehicle.make} ${vehicle.model}`
        : 'Unknown vehicle'

      ;(repairTruck.work_notes ?? []).forEach((workNote, noteIndex) => {
        if (workNote.media?.type !== 'invoice' || !workNote.media.storage_path) return
        const uploadedAtMs = workNote.timestamp?.toMillis?.() ?? 0
        items.push({
          id: `${repairTruck.id}_${noteIndex}_${workNote.media.storage_path}`,
          storagePath: workNote.media.storage_path,
          uploadedAtMs,
          uploadedAtLabel: uploadedAtMs
            ? dayjs(uploadedAtMs).format('MMM D, YYYY h:mm A')
            : '—',
          userName: workNote.user_name,
          note: workNote.note?.trim() || undefined,
          vehicleLabel,
          stockNumber: vehicle?.stock_number,
          vin: vehicle?.vin,
        })
      })
    })

    return items.sort((a, b) => b.uploadedAtMs - a.uploadedAtMs)
  }, [location?.id, shopRepairTrucks, transportTruckVehicleById])

  const totalSpentAtShop = useMemo(() => {
    if (!location?.id) return 0
    return getRepairShopExpenseTotal(expenses, location.id)
  }, [expenses, location?.id])
  const trucksAtShop = useMemo(() => {
    if (!location?.id) return []
    const nowMs = Date.now()

    return transportTrucksWithVehicles
      .filter(
        (item) =>
          getTruckStatus(item.truck) === 'repair' &&
          item.truck.currentLocationId === location.id,
      )
      .map((item): TruckAtShopItem => {
        const latestRepairEntry = [...(item.truck.status_history ?? [])]
          .reverse()
          .find((historyItem) => historyItem.status === 'repair')
        const enteredRepairAtMs = latestRepairEntry?.entered_at?.toMillis?.() ?? nowMs
        const timeInRepairMinutes = Math.max(0, (nowMs - enteredRepairAtMs) / 60000)
        return {
          vehicle: item.vehicle,
          timeInRepair: formatMinutesToTimeInRepair(timeInRepairMinutes),
          timeInRepairMinutes,
        }
      })
  }, [transportTrucksWithVehicles, location?.id])

  const averageTimeInRepair = useMemo(() => {
    if (trucksAtShop.length === 0) return '—'
    const totalMinutes = trucksAtShop.reduce(
      (sum, item) => sum + item.timeInRepairMinutes,
      0,
    )
    const avgMinutes = totalMinutes / trucksAtShop.length
    return formatMinutesToTimeInRepair(avgMinutes)
  }, [trucksAtShop])

  const columnDefs = useMemo<ColDef<TruckAtShopItem>[]>(
    () => [
      {
        colId: 'actions',
        headerName: 'Actions',
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        sortable: false,
        filter: false,
        pinned: 'left',
        lockPosition: true,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0 4px',
        },
        cellRenderer: (params: { data?: TruckAtShopItem }) => (
          <Tooltip title={params.data?.vehicle ? 'View timeline' : 'No vehicle data'}>
            <span>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation()
                  setTimelineVehicle(params.data?.vehicle ?? null)
                }}
                disabled={!params.data?.vehicle}
                aria-label="View timeline"
              >
                <TimelineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
      {
        headerName: 'Vehicle',
        valueGetter: (params) => {
          const v = params.data?.vehicle
          return v ? `${v.year} ${v.make} ${v.model}` : ''
        },
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'vehicle.vin',
        headerName: 'VIN',
        valueGetter: (params) => params.data?.vehicle?.vin ?? '—',
        flex: 0,
        minWidth: 120,
      },
      {
        field: 'vehicle.stock_number',
        headerName: 'Stock #',
        valueGetter: (params) => params.data?.vehicle?.stock_number ?? '',
        flex: 0,
        minWidth: 90,
      },
      {
        headerName: 'Sold To',
        valueGetter: (params) =>
          params.data?.vehicle?.sold_to_user?.name ?? '—',
        flex: 0,
        minWidth: 120,
      },
      {
        field: 'timeInRepair',
        headerName: 'Time Spent',
        flex: 0,
        minWidth: 110,
      },
    ],
    [],
  )

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      editable: false,
    }),
    [],
  )

  if (!location) return null

  const shopTitle = location.name || location.contact_name || 'Repair Shop'
  const addressLine = [
    location.address,
    [location.city, location.state].filter(Boolean).join(', '),
    location.zip,
  ]
    .filter(Boolean)
    .join(' · ')

  const ratingFields: Array<{ key: 'communication' | 'speed' | 'price'; label: string }> = [
    { key: 'communication', label: 'Communication' },
    { key: 'speed', label: 'Speed' },
    { key: 'price', label: 'Price' },
  ]
  const hasAnyRating = ratingFields.some((f) => location[f.key] != null)

  const primaryPhone = formatPhoneDigits(location.contact_phone)
  const secondaryPhone = formatPhoneDigits(location.secondary_phone)
  const laborRate =
    location.labor_rate != null
      ? `$${Number(location.labor_rate).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}/hr`
      : null

  const detailItems: DetailItemProps[] = []
  if (location.contact_name) {
    detailItems.push({
      icon: <PersonOutlineIcon fontSize="small" />,
      label: 'Contact',
      value: location.contact_name,
    })
  }
  if (primaryPhone) {
    detailItems.push({
      icon: <PhoneIcon fontSize="small" />,
      label: 'Phone',
      value: <a href={`tel:${location.contact_phone}`} style={{ color: 'inherit' }}>{primaryPhone}</a>,
    })
  }
  if (secondaryPhone) {
    detailItems.push({
      icon: <PhoneIcon fontSize="small" />,
      label: 'Secondary phone',
      value: <a href={`tel:${location.secondary_phone}`} style={{ color: 'inherit' }}>{secondaryPhone}</a>,
    })
  }
  if (laborRate) {
    detailItems.push({
      icon: <AttachMoneyIcon fontSize="small" />,
      label: 'Labor rate',
      value: laborRate,
    })
  }
  detailItems.push({
    icon: <LocalGasStationIcon fontSize="small" />,
    label: 'Diesel service',
    value: location.diesel ? 'Available' : 'Not available',
  })
  if (location.pickup_date) {
    detailItems.push({
      icon: <EventAvailableIcon fontSize="small" />,
      label: 'Pickup date',
      value: location.pickup_date,
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: { xs: 0, md: 2 } } }}
    >
      {/* Styled header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: 'primary.contrastText',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.18),
                color: 'primary.contrastText',
                width: 48,
                height: 48,
              }}
            >
              <BuildIcon />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }} noWrap>
                {shopTitle}
              </Typography>
              {addressLine && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, opacity: 0.9 }}>
                  <LocationOnIcon sx={{ fontSize: 16 }} />
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {addressLine}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            {onEdit && (
              <Tooltip title="Edit shop information">
                <IconButton
                  onClick={() => onEdit(location)}
                  size="small"
                  aria-label="Edit shop information"
                  sx={{ color: 'primary.contrastText' }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close"
              sx={{ color: 'primary.contrastText' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Shop type + grade chips */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5, pl: { sm: 8 } }}>
          {location.grade && (
            <Chip
              label={`Grade ${location.grade}`}
              size="small"
              color={getGradeColor(location.grade)}
              sx={{ fontWeight: 700, height: 24 }}
            />
          )}
          {location.shop_type?.map((type) => {
            const chipColor = getShopTypeColor(type, theme)
            return (
              <Chip
                key={type}
                icon={getShopTypeIcon(type)}
                label={getShopTypeLabel(type)}
                size="small"
                sx={{
                  height: 24,
                  fontWeight: 600,
                  bgcolor: 'background.paper',
                  color: chipColor,
                  '& .MuiChip-icon': { color: chipColor },
                }}
              />
            )
          })}
        </Stack>
      </Box>

      <DialogContent sx={{ bgcolor: 'background.default' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {/* Metrics at top */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <MetricCard
              icon={<DirectionsCarIcon />}
              label="Trucks at shop"
              value={trucksAtShop.length}
              color={theme.palette.primary.main}
            />
            <MetricCard
              icon={<AccessTimeIcon />}
              label="Avg. time spent"
              value={averageTimeInRepair}
              color={theme.palette.secondary.main}
            />
            <MetricCard
              icon={<BuildIcon />}
              label="Capacity"
              value={
                location.capacity != null
                  ? `${trucksAtShop.length} / ${location.capacity}`
                  : '—'
              }
              color={theme.palette.success.main}
            />
            <MetricCard
              icon={<AttachMoneyIcon />}
              label="Total spent"
              value={formatCurrency(totalSpentAtShop) || '$0.00'}
              color={theme.palette.warning.main}
            />
          </Box>

          {/* Shop details */}
          {detailItems.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Shop details
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                  gap: 2,
                }}
              >
                {detailItems.map((item) => (
                  <DetailItem key={item.label} {...item} />
                ))}
              </Box>
            </Paper>
          )}

          {/* Ratings */}
          {hasAnyRating && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Ratings
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: 2,
                }}
              >
                {ratingFields.map(({ key, label }) => (
                  <Box key={key}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {label}
                    </Typography>
                    {location[key] != null ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Rating
                          value={location[key] as number}
                          precision={0.5}
                          size="small"
                          readOnly
                          sx={{ '& .MuiRating-iconFilled': { color: 'primary.main' } }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {(location[key] as number).toFixed(1)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not rated
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          {/* Notes */}
          {location.notes && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <NotesIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  Notes
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {location.notes}
              </Typography>
            </Paper>
          )}

          {/* Uploaded invoices */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PictureAsPdfIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="subtitle1" fontWeight={700}>
                Uploaded invoices
              </Typography>
              {!shopInvoicesLoading && shopInvoices.length > 0 && (
                <Chip label={shopInvoices.length} size="small" sx={{ height: 22 }} />
              )}
            </Box>
            {shopInvoicesLoading ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                }}
              >
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  Loading invoices…
                </Typography>
              </Paper>
            ) : shopInvoices.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 2,
                  textAlign: 'center',
                  borderStyle: 'dashed',
                }}
              >
                <PictureAsPdfIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No invoice PDFs uploaded for this shop yet.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {shopInvoices.map((invoice) => (
                  <Paper key={invoice.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {invoice.vehicleLabel}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {[
                            invoice.stockNumber ? `Stock #${invoice.stockNumber}` : null,
                            invoice.vin ? `VIN ${invoice.vin}` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {invoice.uploadedAtLabel}
                          {invoice.userName ? ` · ${invoice.userName}` : ''}
                        </Typography>
                        {invoice.note && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}
                          >
                            {invoice.note}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ flexShrink: 0 }}>
                        <ShopInvoicePdfLink storagePath={invoice.storagePath} />
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>

          <Divider />

          {/* Trucks table */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Trucks at this shop
            </Typography>
            {trucksAtShop.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 2,
                  textAlign: 'center',
                  borderStyle: 'dashed',
                }}
              >
                <DirectionsCarIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No trucks currently at this repair shop.
                </Typography>
              </Paper>
            ) : isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {trucksAtShop.map((item, index) => {
                  const v = item.vehicle
                  return (
                    <Paper
                      key={item.vehicle?.id ?? index}
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2 }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {v ? `${v.year} ${v.make} ${v.model}` : '—'}
                        </Typography>
                        <Tooltip title={v ? 'View timeline' : 'No vehicle data'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setTimelineVehicle(v ?? null)}
                              disabled={!v}
                              aria-label="View timeline"
                            >
                              <TimelineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'auto 1fr',
                          gap: 0.5,
                          columnGap: 2,
                          typography: 'body2',
                          color: 'text.secondary',
                          mt: 1,
                        }}
                      >
                        <span>VIN</span>
                        <span>{v?.vin ?? '—'}</span>
                        <span>Stock #</span>
                        <span>{v?.stock_number ?? '—'}</span>
                        <span>Sold To</span>
                        <span>{v?.sold_to_user?.name ?? '—'}</span>
                        <span>Time Spent</span>
                        <span>{item.timeInRepair}</span>
                      </Box>
                    </Paper>
                  )
                })}
              </Box>
            ) : (
              <div
                className={
                  theme.palette.mode === 'dark'
                    ? 'ag-theme-alpine-dark'
                    : 'ag-theme-alpine'
                }
                style={{ height: 300, width: '100%' }}
              >
                <AgGridReact<TruckAtShopItem>
                  rowData={trucksAtShop}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  domLayout="normal"
                  animateRows={false}
                  suppressCellFocus
                />
              </div>
            )}
          </Box>
        </Box>
      </DialogContent>
      {timelineVehicle && (
        <TimelineDialog
          open={Boolean(timelineVehicle)}
          onClose={() => setTimelineVehicle(null)}
          vehicle={timelineVehicle}
        />
      )}
    </Dialog>
  )
}

export default ShopMetricsDialog
